/**
 * Mirrors `Skill2era.Application.DTOs.CourseMaterialDto` from the backend
 * swagger. Used by:
 *   - GET    /api/students/me/course-materials       (own list)
 *   - GET    /api/students/me/course-materials/{id}/download  (binary)
 *   - GET    /api/professor/course-materials         (prof view)
 *   - GET    /api/admin/course-materials/...         (admin view)
 *
 * The backend sends `category` as a string label (e.g. "Pdf", "Video"). The
 * upload form uses an integer enum (1=Pdf, 2=Video, 3=Image, 4=Slide,
 * 5=Document, 6=Audio, 99=Other) — that mapping is documented below for the
 * upload form on the professor side.
 */
export type CourseMaterialCategoryLabel =
  | 'Pdf'
  | 'Video'
  | 'Image'
  | 'Slide'
  | 'Document'
  | 'Audio'
  | 'Other';

/** Numeric enum values accepted by the upload endpoint. */
export enum CourseMaterialCategory {
  Pdf = 1,
  Video = 2,
  Image = 3,
  Slide = 4,
  Document = 5,
  Audio = 6,
  Other = 99,
}

export interface CourseMaterial {
  id: number;
  title: string;
  description?: string | null;
  /** Backend returns the string label (e.g. "Pdf"). */
  category: CourseMaterialCategoryLabel | string;

  learningPathId: number;
  learningPathTitle?: string | null;
  subjectId?: number | null;
  subjectName?: string | null;
  themeId?: number | null;
  themeTitle?: string | null;
  groupId?: number | null;
  groupName?: string | null;

  professorId: number;
  professorFullName?: string | null;

  isPublic: boolean;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  contentType?: string | null;
  extension?: string | null;
  externalUrl?: string | null;

  isArchived: boolean;
  createdAt: string;
  updatedAt?: string | null;

  /**
   * URL presignee de lecture directe sur le stockage, nom d'origine force en
   * piece jointe. Nulle pour un lien externe, ou pour un support herite
   * encore range sur le disque.
   *
   * <p>Le serveur la calculait deja, le front ne la declarait pas : le
   * telechargement passait donc par l'API, qui relayait le flux. Une video de
   * 1,5 Go y mourait deux fois — le serveur tenait la requete ouverte pour
   * rien, et le navigateur tentait de bufferiser le tout en memoire.</p>
   */
  previewUrl?: string | null;

  /** Relative path to the auth-protected download endpoint, e.g.
   *  `/api/students/me/course-materials/42/download`. May be null when
   *  the material is an external link only. */
  /**
   * Un fichier est-il attaché ? Le chemin de téléchargement dépend du rôle
   * (`admin` / `professor` / `students/me`), c'est donc au client d'appeler son
   * propre endpoint — le serveur n'expose que le fait.
   */
  hasFile?: boolean;

  /** Séance dont ce support est l'enregistrement. Null s'il a été déposé librement. */
  appointmentId?: number | null;

  /** Le support est-il épinglé dans les favoris de l'élève courant ? */
  isFavorite?: boolean;
}

/**
 * Enregistrement d'un support déjà téléversé sur le stockage — second temps du
 * dépôt présigné, une fois le PUT terminé. Le fichier n'a jamais traversé
 * l'API : seule sa clé arrive ici.
 */
export interface RegisterCourseMaterialDto {
  title: string;
  description?: string | null;
  /** Null = diffusion à tous les parcours. */
  learningPathId: number | null;
  subjectId?: number | null;
  themeId?: number | null;
  groupId?: number | null;
  isPublic: boolean;
  /** Clé rendue par `/api/uploads/presign`. */
  objectKey: string;
  /** Nom d'origine, restitué au téléchargement (la clé est opaque). */
  fileName: string;
  contentType?: string | null;
  sizeBytes?: number | null;
  /** Séance dont ce support est l'enregistrement. */
  appointmentId?: number | null;
}

/** Payload for POST multipart upload (fields sent as JSON part or separate fields). */
export interface CreateCourseMaterialDto {
  title: string;
  description?: string;
  /** Numeric enum: 1=Pdf, 2=Video, 3=Image, 4=Slide, 5=Document, 6=Audio, 99=Other */
  /** Null = diffusion à tous les parcours (règlement, note d'information). */
  learningPathId: number | null;
  subjectId?: number | null;
  themeId?: number | null;
  groupId?: number | null;
  isPublic: boolean;
  externalUrl?: string | null;
  // The actual file is added to FormData separately under key "file"
}

/** Payload for PUT metadata update (title/description/isPublic only). */
export interface UpdateCourseMaterialDto {
  title?: string;
  description?: string;
  isPublic?: boolean;
  externalUrl?: string | null;
}

/** Les trois états de diffusion d'un support. */
export type MaterialStatus = 'published' | 'unpublished' | 'archived';

/**
 * Statut de diffusion, déduit des deux drapeaux existants.
 *
 * <p>Aucune colonne ne le porte, et c'est voulu : l'archivage et la diffusion
 * sont deux décisions indépendantes, et une troisième colonne les résumant
 * finirait par les contredire.</p>
 *
 * <p>L'ordre compte. Un support archivé est archivé, quelle qu'ait été sa
 * diffusion. Et un support restreint à un groupe est <b>diffusé</b> à ce
 * groupe : le ranger parmi les non publiés laisserait croire qu'il attend une
 * décision déjà prise.</p>
 *
 * <p>La règle vit ici plutôt que dans chaque écran : la carte l'affiche, la
 * bibliothèque filtre dessus, et deux copies auraient fini par diverger.</p>
 */
export function materialStatus(
  m: Pick<CourseMaterial, 'isArchived' | 'isPublic' | 'groupId'> | null | undefined,
): MaterialStatus {
  if (!m) return 'unpublished';
  if (m.isArchived) return 'archived';
  if (m.isPublic || m.groupId != null) return 'published';
  return 'unpublished';
}
